import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  alpha,
  useTheme,
  IconButton,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  IVAdjustmentSettings,
  OC_DEFAULT_IV_SETTINGS,
  PositionLeg,
  PositionShares,
} from 'types/optionsCalculator';
import { OptionType, VolSkewBaseLine } from 'types';
import EditableOptionLeg from './EditableOptionLeg';
import EditableUnderlyingPosition from './EditableUnderlyingPosition';
import { dayjs } from 'util/shared';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import TabbedContent from 'components/shared/TabbedContent';
import { TabItem } from 'types/shared';
import BellCurve from 'icons/BellCurve';
import VolSkewChart from 'components/implied_vol/VolSkew/VolSkewChart';
import SliderFilter from 'components/shared/SliderFilter';
import OptionsDropdownSelector from 'components/shared/OptionsDropdownSelector';
import { IVOL_SKEW_BASE_LINE_LABELS_MAP } from 'config/iVol';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { useOptionsCalculatorActions } from 'hooks/optionsCalculator/useOptionsCalculatorActions';

const MILLISECONDS_PER_DAY = 1_000 * 60 * 60 * 24;

interface GroupedOptionLegsProps {
  legs: PositionLeg[];
  expiration: number;
  expirationOptions: number[];
  onAddLeg: (leg: PositionLeg) => void;
  onEditLeg: (legId: string, updatedLeg: PositionLeg) => void;
  onDeleteLeg: (legId: string) => void;
  onDeleteLegsBulk: (legIds: string[]) => void;
  expirationsIVSettingsMap: Map<
    PositionLeg['expiration'],
    IVAdjustmentSettings
  >;
  onUpdateExpirationsIVSettingsMap: (
    expiration: PositionLeg['expiration'],
    ivSettings: IVAdjustmentSettings,
  ) => void;
}

const GroupedOptionLegs = ({
  legs,
  expiration,
  expirationOptions,
  onAddLeg,
  onEditLeg,
  onDeleteLeg,
  onDeleteLegsBulk,
  expirationsIVSettingsMap,
  onUpdateExpirationsIVSettingsMap,
}: GroupedOptionLegsProps) => {
  const theme = useTheme();
  const { sym } = useOptionsCalculatorData();
  const [expanded, setExpanded] = useState<boolean>(true);
  const [showIvSettings, setShowIvSettings] = useState<boolean>(false);
  const [localIvSettings, setLocalIvSettings] = useState<IVAdjustmentSettings>(
    expirationsIVSettingsMap.get(expiration) ?? OC_DEFAULT_IV_SETTINGS,
  );

  const [currentBaseLine, setCurrentBaseLine] = useState<VolSkewBaseLine>(
    VolSkewBaseLine.AtmIV,
  );

  if (legs.length === 0) {
    return null;
  }

  const defaultLeg: PositionLeg = {
    id: `${Date.now()}`,
    type: OptionType.CALL,
    strike: 100,
    quantity: 1,
    expiration,
    price: 10,
    sym,
  };

  const handleRemoveAllLegs = () => {
    onDeleteLegsBulk(legs.map((leg) => leg.id));
  };

  const handleToggleIvSettings = () => setShowIvSettings((prev) => !prev);

  const handleUpdateIvSettings = (settings: IVAdjustmentSettings) => {
    setLocalIvSettings(settings);
  };

  const handleUpdateCurrentBaseLine = (line: VolSkewBaseLine) => {
    setCurrentBaseLine(line);
  };

  const handleSaveIvSettings = () => {
    onUpdateExpirationsIVSettingsMap(expiration, localIvSettings);
    setShowIvSettings(false);
  };

  const handleAddLeg = () => {
    onAddLeg({
      ...defaultLeg,
      expiration: expiration,
    });
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Stack
        sx={{
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          py: 2,
          px: {
            xs: 2,
            sm: 4,
          },
          justifyContent: 'space-between',
          alignItems: {
            xs: 'flex-start',
            sm: 'center',
          },
          backgroundColor: alpha(theme.palette.primary.main, 0.3),
          borderTopRightRadius: '8px',
          borderTopLeftRadius: '8px',
          gap: {
            xs: 2,
            sm: 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <IconButton
            color="primary"
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <Typography variant="body1" fontWeight="bold">
            {dayjs(expiration).format('YYYY-MM-DD')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            color="success"
            onClick={handleToggleIvSettings}
            sx={{
              borderRadius: '20px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
              },
              fontSize: '12px',
              textTransform: 'none',
              minWidth: { xs: '110px', sm: 'auto' },
            }}
            startIcon={<BellCurve />}
          >
            IV Settings
          </Button>

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            color="primary"
            size="small"
            onClick={handleAddLeg}
            sx={{
              borderRadius: '20px',
              fontSize: '12px',
              textTransform: 'none',
              minWidth: { xs: '100px', sm: 'auto' },
            }}
          >
            Add Leg
          </Button>
          <Button
            startIcon={<DeleteIcon />}
            variant="outlined"
            color="error"
            size="small"
            onClick={handleRemoveAllLegs}
            sx={{
              borderRadius: '20px',
              fontSize: '12px',
              textTransform: 'none',
              minWidth: { xs: '110px', sm: 'auto' },
            }}
          >
            Delete All
          </Button>
        </Box>
      </Stack>

      <Collapse in={expanded}>
        <Box
          sx={{
            pl: '32px',
          }}
        >
          <Stack
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderBottomRightRadius: '8px',
              borderBottomLeftRadius: '8px',
            }}
          >
            {legs.map((leg, index) => (
              <Fragment key={`${leg.id}`}>
                {index > 0 && (
                  <Divider
                    sx={{
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      my: 1,
                    }}
                  />
                )}
                <EditableOptionLeg
                  leg={leg}
                  onEditLeg={(legId, updatedLeg) => {
                    onEditLeg(legId, updatedLeg as PositionLeg);
                  }}
                  onDeleteLeg={(legId) => {
                    onDeleteLeg(legId);
                  }}
                  expirationOptions={expirationOptions}
                />
              </Fragment>
            ))}
          </Stack>
        </Box>
      </Collapse>

      <Dialog
        open={showIvSettings}
        onClose={handleToggleIvSettings}
        maxWidth="lg"
        fullWidth
        key={`${expiration}-${showIvSettings}`}
      >
        <DialogTitle>IV Settings</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              transition: '0.5s',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              height: {
                xs: '500px',
                md: '600px',
              },
            }}
          >
            <IVSettings
              sym={sym}
              ivSettings={localIvSettings}
              onUpdateIvSettings={handleUpdateIvSettings}
              defaultExpiration={expiration}
              currentBaseLine={currentBaseLine}
              onUpdateCurrentBaseLine={handleUpdateCurrentBaseLine}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={handleToggleIvSettings}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveIvSettings}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface IVSettingsProps {
  sym: string;
  ivSettings: IVAdjustmentSettings;
  currentBaseLine: VolSkewBaseLine;
  onUpdateIvSettings: (settings: IVAdjustmentSettings) => void;
  onUpdateCurrentBaseLine: (line: VolSkewBaseLine) => void;
  defaultExpiration: number;
}

const IVSettings = ({
  sym,
  ivSettings,
  onUpdateIvSettings,
  currentBaseLine,
  onUpdateCurrentBaseLine,
  defaultExpiration,
}: IVSettingsProps) => {
  const theme = useTheme();
  const vsRef = useRef<HTMLDivElement | null>(null);

  const displayValueFormatter = useCallback((value: number | number[]) => {
    if (Array.isArray(value)) {
      return `${value[0]} - ${value[1]}x`;
    }
    return `${value}x`;
  }, []);

  const handleResetToDefaults = useCallback(() => {
    onUpdateIvSettings(OC_DEFAULT_IV_SETTINGS);
  }, [onUpdateIvSettings]);

  return (
    <Grid container sx={{ height: '100%', p: 2 }} spacing={4}>
      <Grid item xs={12} md={3}>
        <Stack sx={{ gap: 4 }}>
          {/* Base Line Selection Section */}
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              p: 4,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 4,
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              Select a Base Line
            </Typography>
            <OptionsDropdownSelector
              label="Base Line"
              value={currentBaseLine}
              options={Object.values(VolSkewBaseLine).map((line) => ({
                label: IVOL_SKEW_BASE_LINE_LABELS_MAP[line],
                value: line,
              }))}
              isMultiple={false}
              onChange={(event) =>
                onUpdateCurrentBaseLine(event.target.value as VolSkewBaseLine)
              }
              sx={{ maxWidth: '100%' }}
            />
          </Box>

          {/* IV Adjustments Section */}
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              p: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                Adjust the IV Curve
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetToDefaults}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  px: 2,
                }}
              >
                Reset
              </Button>
            </Box>
            <Stack sx={{ gap: 2 }}>
              <SliderFilter
                header="Parallel Shift"
                name="levelShift"
                min={-1}
                max={2}
                step={0.01}
                value={ivSettings.levelShift}
                onChange={(newValue) =>
                  onUpdateIvSettings({
                    ...ivSettings,
                    levelShift: newValue as number,
                  })
                }
                valueFormat={'number'}
                displayValueFormatter={displayValueFormatter}
              />
              <SliderFilter
                header="Call Skew (Steepen/Flatten)"
                name="callSkew"
                min={0.1}
                max={5.0}
                step={0.05}
                value={ivSettings.callSlopeMultiplier}
                onChange={(newValue) =>
                  onUpdateIvSettings({
                    ...ivSettings,
                    callSlopeMultiplier: newValue as number,
                  })
                }
                valueFormat={'number'}
                displayValueFormatter={displayValueFormatter}
              />
              <SliderFilter
                header="Put Skew (Steepen/Flatten)"
                name="putSkew"
                min={0.1}
                max={5.0}
                step={0.05}
                value={ivSettings.putSlopeMultiplier}
                onChange={(newValue) =>
                  onUpdateIvSettings({
                    ...ivSettings,
                    putSlopeMultiplier: newValue as number,
                  })
                }
                valueFormat={'number'}
                displayValueFormatter={displayValueFormatter}
              />
            </Stack>
          </Box>
        </Stack>
      </Grid>

      <Grid item xs={12} md={9}>
        <VolSkewChart
          selectedSym={sym}
          ref={vsRef}
          defaultExpiration={defaultExpiration}
          ivAdjustmentParams={ivSettings}
          currentBaseLine={currentBaseLine}
          useSpotGammaIV
          forceReset
        />
      </Grid>
    </Grid>
  );
};

const PositionContainer = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState<string>('options');

  // Subscribe only to the data we need from context
  const {
    sym,
    legs,
    shares,
    expirationsIVSettingsMap,
    greeksData,
    currentPrice,
    baseDate,
  } = useOptionsCalculatorData();

  const {
    addLeg,
    removeLeg,
    updateLeg,
    removeLegsBulk,
    updateShares,
    updateExpirationsIVSettingsMap,
    updateBaseDate,
  } = useOptionsCalculatorActions();

  // Calculate expirations from greeks data AND include all leg expirations to prevent "out of range" errors
  const expirations = useMemo(() => {
    const greeksExpirations = greeksData
      ? Object.keys(greeksData).map((key) => Number(key))
      : [];
    const legExpirations = legs.map((leg) => leg.expiration);

    // Combine and deduplicate all expirations
    const allExpirations = [
      ...new Set([...greeksExpirations, ...legExpirations]),
    ];

    // Sort chronologically
    return allExpirations.sort((a, b) => a - b);
  }, [greeksData, legs]);

  const defaultLeg: PositionLeg = useMemo(
    () => ({
      id: `${Date.now()}`,
      type: OptionType.CALL,
      strike: 100,
      quantity: 1,
      expiration: expirations[0] ?? dayjs('2026-01-16').valueOf(), // Fallback if no expirations available
      price: 10,
      sym,
    }),
    [expirations, sym],
  );

  const handleAddShares = useCallback(() => {
    const newShare: PositionShares = {
      sym,
      quantity: 100,
      pricePaid: currentPrice ?? 0,
    };
    updateShares([...shares, newShare]);
  }, [sym, currentPrice, shares, updateShares]);

  const handleEditShares = useCallback(
    (index: number, updatedFields: Partial<PositionShares>) => {
      const updatedShares = [...shares];
      updatedShares[index] = { ...updatedShares[index], ...updatedFields };
      updateShares(updatedShares);
    },
    [shares, updateShares],
  );

  const handleDeleteShares = useCallback(
    (index: number) => {
      const updatedShares = shares.filter((_, i) => i !== index);
      updateShares(updatedShares);
    },
    [shares, updateShares],
  );

  const handleTabChange = useCallback((tab: string) => {
    setCurrentTab(tab);
  }, []);

  const handleAddLeg = useCallback(() => {
    addLeg(defaultLeg);
  }, [addLeg, defaultLeg]);

  // Memoize the grouped legs to prevent unnecessary re-calculations
  const groupedLegs = useMemo(() => {
    return legs.reduce(
      (groups, leg) => {
        const expiry = leg.expiration;
        if (!groups[expiry]) {
          groups[expiry] = [];
        }
        groups[expiry].push(leg);
        return groups;
      },
      {} as Record<number, PositionLeg[]>,
    );
  }, [legs]);

  const latestLegExpiration = useMemo(() => {
    return Math.max(...Object.keys(groupedLegs).map(Number));
  }, [groupedLegs]);

  const optionsTabContent = (
    <>
      {legs.length === 0 ? (
        <Stack
          spacing={3}
          alignItems="center"
          height="100%"
          justifyContent="center"
        >
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No option positions added yet
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddLeg}
            sx={{ textTransform: 'none' }}
          >
            Add Your First Option Leg
          </Button>
        </Stack>
      ) : (
        <Stack spacing={3} mt={4}>
          {/* Group legs by expiration date */}
          <Stack spacing={4}>
            {Object.entries(groupedLegs).map(
              ([expirationStr, positionLegs]) => {
                const expiration = Number(expirationStr);
                return (
                  <GroupedOptionLegs
                    key={`${expiration}-${positionLegs.length}`}
                    legs={positionLegs}
                    expiration={expiration}
                    expirationOptions={expirations}
                    onAddLeg={addLeg}
                    onEditLeg={updateLeg}
                    onDeleteLeg={removeLeg}
                    onDeleteLegsBulk={removeLegsBulk}
                    expirationsIVSettingsMap={expirationsIVSettingsMap}
                    onUpdateExpirationsIVSettingsMap={
                      updateExpirationsIVSettingsMap
                    }
                  />
                );
              },
            )}
          </Stack>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddLeg}
              sx={{
                textTransform: 'none',
              }}
            >
              Add New Leg
            </Button>
          </Box>
        </Stack>
      )}
    </>
  );

  const sharesTabContent = (
    <>
      {shares.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.action.hover, 0.5),
            borderRadius: 1,
            mb: 2,
          }}
        >
          <Typography color="text.secondary">
            No underlying positions added yet
          </Typography>
        </Box>
      ) : (
        <Stack
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: '8px',
            p: 1,
            mt: 4,
          }}
        >
          {shares.map((share, index) => (
            <EditableUnderlyingPosition
              key={index}
              shares={share}
              onEditShares={(updatedFields) =>
                handleEditShares(index, updatedFields)
              }
              onDeleteShares={() => handleDeleteShares(index)}
            />
          ))}
        </Stack>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddShares}
          sx={{ textTransform: 'none' }}
        >
          Add Underlying Position
        </Button>
      </Box>
    </>
  );

  const tabs: TabItem[] = [
    {
      label: 'Options',
      value: 'options',
      content: optionsTabContent,
      actions: [],
    },
    {
      label: 'Underlying',
      value: 'shares',
      content: sharesTabContent,
      actions: [],
    },
  ];

  const displayValueFormatter = useCallback(
    (value: number | number[]) => {
      if (Array.isArray(value)) {
        const startDate = dayjs(value[0]);
        const endDate = dayjs(value[1]);
        const startDaysRemaining = Math.max(
          0,
          Math.ceil((latestLegExpiration - value[0]) / MILLISECONDS_PER_DAY),
        );
        const endDaysRemaining = Math.max(
          0,
          Math.ceil((latestLegExpiration - value[1]) / MILLISECONDS_PER_DAY),
        );

        return `${startDate.format('YYYY-MM-DD')} (${startDaysRemaining}d) - ${endDate.format('YYYY-MM-DD')} (${endDaysRemaining}d)`;
      }

      const selectedDate = dayjs(value);
      const daysRemaining = Math.max(
        0,
        Math.ceil((latestLegExpiration - value) / MILLISECONDS_PER_DAY),
      );

      return `${selectedDate.format('YYYY-MM-DD')} (${daysRemaining}d remaining)`;
    },
    [latestLegExpiration],
  );

  return (
    <Stack sx={{ px: 3, py: 1.5 }}>
      <Stack
        sx={{
          gap: 3,
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Typography
          sx={{ fontWeight: 'bold', fontSize: { xs: '0.6rem', sm: '0.8rem' } }}
        >
          Positions ({legs.length} leg{legs.length === 1 ? '' : 's'},{' '}
          {shares.length} share{shares.length === 1 ? '' : 's'})
        </Typography>

        <Box
          sx={{
            px: '8px',
            minWidth: { xs: '270px', sm: '360px', md: '500px' },
          }}
        >
          <SliderFilter
            header="Date"
            name="date"
            min={dayjs().valueOf()}
            max={latestLegExpiration}
            step={1}
            value={baseDate}
            onChange={(newValue) => {
              updateBaseDate(newValue as number);
            }}
            valueFormat="date"
            displayValueFormatter={displayValueFormatter}
          />
        </Box>
      </Stack>

      <TabbedContent
        tabs={tabs}
        defaultTab={currentTab}
        onChange={handleTabChange}
        staticActions={[]}
        sx={{ px: 0, py: 0, height: '100%' }}
      />
    </Stack>
  );
};

export default PositionContainer;
